import { useEffect, useRef, useState } from 'react'
import { serverUrl } from '../../../utilites/config'
import { reconnectingSocket } from '../../../utilites/socket'

const TELESCOPE_POSITION_URL = `${serverUrl}/telescope/position`
const TELESCOPE_POSITION_TOPIC = '/topic/telescope-position'
const HTTP_NOT_FOUND = 404

export type TelescopePosition = {
  azimuthDegrees: number
  elevationDegrees: number
  targetName?: string
}

export function parseTelescopePosition(body: string): TelescopePosition | undefined {
  try {
    const position = JSON.parse(body) as Partial<TelescopePosition>
    if (typeof position.azimuthDegrees !== 'number') return undefined
    if (typeof position.elevationDegrees !== 'number') return undefined
    return {
      azimuthDegrees: position.azimuthDegrees,
      elevationDegrees: position.elevationDegrees,
      targetName: typeof position.targetName === 'string' ? position.targetName : undefined,
    }
  } catch {
    return undefined
  }
}

// Latest telescope pointing: initial REST fetch, then live updates over STOMP.
export function useTelescopePosition() {
  const [position, setPosition] = useState<TelescopePosition>()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const socketRef = useRef<{ disconnect?: () => void } | null>(null)

  useEffect(() => {
    const controller = new AbortController()

    const fetchPosition = async () => {
      try {
        const response = await fetch(TELESCOPE_POSITION_URL, { signal: controller.signal })

        if (response.status === HTTP_NOT_FOUND) {
          setPosition(undefined)
          setError(false)
          return
        }

        if (!response.ok) {
          throw new Error(`Telescope position request failed with status ${response.status}`)
        }

        setPosition((await response.json()) as TelescopePosition)
        setError(false)
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        setError(true)
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    const handleMessage = (message: { body: string }) => {
      const next = parseTelescopePosition(message.body)
      if (!next) return
      setPosition(next)
      setError(false)
      setIsLoading(false)
    }

    fetchPosition()
    socketRef.current = reconnectingSocket(
      serverUrl + '/websocket',
      handleMessage,
      TELESCOPE_POSITION_TOPIC,
      window.location.href,
    )

    return () => {
      controller.abort()
      socketRef.current?.disconnect?.()
      socketRef.current = null
    }
  }, [])

  return { position, isLoading, error }
}
